import type { FC } from "react";
import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { DonutChart, LEGEND_POSITIONS } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { keywordGroupsTransforms, keywordGroupsQueryClient } from "app/services/keyword-groups";
import type { Filter } from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import { EGridDirections } from "app/components/React/Legends/legendsTypes";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";
// eslint-disable-next-line max-len
import { LegendsContentContainer } from "app/pages/digital-marketing/keywords-campaigns/reports/serp-feature-report/composition-charts/pie-chart/SERPReportCompositionPieChartStyledComponents";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { getValueAsPercentage } from "../serp-composition/serpCompositionUtils";
import { VOLUME_AND_NO_CLICKS_ENDPOINT } from "app/common/keyword-research/keywords-overview/volumeAndClicksConsts";

import { DONUT_INNER_SIZE, WIDGET_TITLE_KEY, WIDGET_TOOLTIP_KEY, devicesConfig } from "./deviceDistributionConsts";
import { getConfig } from "./deviceDistributionGraphConfig";
import type { IDeviceDistributionGraphItem, IDeviceDistributionResponseData } from "./deviceDistributionTypes";
import { getTransformedData } from "./deviceDistributionUtils";

const DeviceDistributionContainer = styled.div`
    width: 50%;
`;

const DeviceDistributionContentContainer = styled(FlexColumn)`
    height: 100%;
    justify-content: center;
    gap: 24px;
    flex: 1 0 0;
    padding: 0 0 0 96px;
    align-self: stretch;
    @media (max-width: 1360px) {
        padding-left: 32px;
    }
`;

const DeviceDistributionLoader = () => (
    <Flex
        justifyContent="flex-start"
        alignItems="center"
        gap="25%"
    >
        <PixelPlaceholderLoader
            height={148}
            width={148}
        />
        <Flex
            gap="16px"
            flexDirection="column"
            flexShrink={1}
        >
            <PixelPlaceholderLoader
                height={56}
                width={100}
            />
            <PixelPlaceholderLoader
                height={56}
                width={100}
            />
        </Flex>
    </Flex>
);

interface IDeviceDistribution {
    subtitleFilters: Filter[];
}

export const DeviceDistribution: FC<IDeviceDistribution> = ({ subtitleFilters = [] }) => {
    const { params } = useCurrentStateAndParams();
    const { keyword, duration, country } = params;
    const [isLoading, setIsLoading] = useState(true);
    const [distributionData, setDistributionData] = useState<IDeviceDistributionGraphItem[]>([]);

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            const durationObject = DurationService.getDurationData(duration);
            const { from, to, isWindow } = durationObject.forAPI;
            const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(keyword);
            const keys = isKeywordGroup ? keyword.substring(1) : keyword;

            const apiParams = {
                to,
                from,
                country,
                isWindow,
                keys,
                webSource: devicesTypes.TOTAL,
                sourceType: SourceTypeMap.Total,
                GetlAllAvailableTrend: true,
                ...(isKeywordGroup && {
                    GroupHash: (await keywordGroupsQueryClient.getGroupById(keys))?.GroupHash,
                }),
            };

            try {
                const volumeData = await DefaultFetchService.getInstance().get<IDeviceDistributionResponseData>(
                    VOLUME_AND_NO_CLICKS_ENDPOINT,
                    apiParams,
                );
                const transformedData = getTransformedData(volumeData, keys);
                setDistributionData(transformedData);
            } catch (error) {
                Sentry.error(`Error fetching device distribution data for keyword analysis overview`, error);
                setDistributionData(devicesConfig.map((config) => ({ ...config, y: 0 })));
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    const hasNoData = distributionData.every((item) => !item.y);

    return (
        <DeviceDistributionContainer data-automation="DeviceDistribution">
            <WidgetContainer
                title={translate(WIDGET_TITLE_KEY)}
                tooltip={translate(WIDGET_TOOLTIP_KEY)}
                subtitleFilters={subtitleFilters}
                isLoading={false}
                noData={false}
                withBorder={true}
                noDataTitle={translate("common.noresults")}
                noDataSubtitle={translate("keyword.analysis.overview.page.v2.trend_over_time.noData.subtitle")}
                height={"330px"}
            >
                <DeviceDistributionContentContainer>
                    {isLoading ? (
                        <DeviceDistributionLoader />
                    ) : (
                        <DonutChart
                            data={[{ data: distributionData }]}
                            config={getConfig(hasNoData)}
                            shouldPresentLegend={true}
                            legendsGridDirection={EGridDirections.COLUMN}
                            shouldPresentValueAtLegend={true}
                            withoutTooltipsPadding={false}
                            legendPosition={LEGEND_POSITIONS.RIGHT}
                            legendsMaxWidth={128}
                            // @ts-expect-error FIXME: Type '(value: number, decimal?: number) => string' is not assignable to type '(value: number, data: PieDataItem) => string'
                            legendValueFilter={getValueAsPercentage}
                            customLegendComponentWrapper={LegendsContentContainer}
                            innerSize={DONUT_INNER_SIZE}
                        />
                    )}
                </DeviceDistributionContentContainer>
            </WidgetContainer>
        </DeviceDistributionContainer>
    );
};
